/**
 * Table of Contents JavaScript
 * 
 * Handles:
 * - Smooth scrolling to headings
 * - Active section highlighting while scrolling
 * - Collapsible toggle functionality
 */

(function() {
    'use strict';

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    function init() {
        var tocs = document.querySelectorAll('.ca-toc');
        
        tocs.forEach(function(toc) {
            initToggle(toc);
            initSmoothScroll(toc);
            
            if (toc.classList.contains('ca-toc--highlight-active')) {
                initActiveHighlight(toc);
            }
        });
    }

    /**
     * Initialize collapsible toggle
     */
    function initToggle(toc) {
        var toggle = toc.querySelector('.ca-toc__toggle');
        var list = toc.querySelector('.ca-toc__list');
        
        if (!toggle || !list) return;

        toggle.addEventListener('click', function() {
            var isExpanded = this.getAttribute('aria-expanded') === 'true';
            var newState = !isExpanded;
            
            this.setAttribute('aria-expanded', String(newState));
            
            if (newState) {
                list.removeAttribute('hidden');
                toc.classList.remove('ca-toc--collapsed');
                toc.classList.add('ca-toc--mobile-open');
            } else {
                list.setAttribute('hidden', '');
                toc.classList.add('ca-toc--collapsed');
                toc.classList.remove('ca-toc--mobile-open');
            }
        });

        // Auto-expand on desktop if collapsed on mobile
        function checkViewport() {
            if (window.innerWidth > 768) {
                if (!toc.classList.contains('ca-toc--collapsed') || 
                    toc.dataset.startCollapsed !== 'true') {
                    list.removeAttribute('hidden');
                    toggle.setAttribute('aria-expanded', 'true');
                }
            }
        }
        
        // Store initial collapsed state
        if (list.hasAttribute('hidden')) {
            toc.dataset.startCollapsed = 'true';
        }
        
        checkViewport();
        window.addEventListener('resize', debounce(checkViewport, 150));
    }

    /**
     * Initialize smooth scrolling for TOC links
     */
    function initSmoothScroll(toc) {
        var links = toc.querySelectorAll('.ca-toc__link');
        
        links.forEach(function(link) {
            link.addEventListener('click', function(e) {
                var href = this.getAttribute('href');
                if (!href || href.charAt(0) !== '#') return;
                
                var target = document.getElementById(href.slice(1));
                if (!target) return;
                
                e.preventDefault();
                
                // Calculate offset (for fixed headers)
                var headerOffset = getHeaderOffset();
                var elementPosition = target.getBoundingClientRect().top;
                var offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
                
                // Update URL hash without jumping
                history.pushState(null, null, href);
                
                // Focus the target for accessibility
                target.setAttribute('tabindex', '-1');
                target.focus({ preventScroll: true });
                
                // Close TOC on mobile after click
                if (window.innerWidth <= 768) {
                    var toggle = toc.querySelector('.ca-toc__toggle');
                    var list = toc.querySelector('.ca-toc__list');
                    if (toggle && list) {
                        toggle.setAttribute('aria-expanded', 'false');
                        list.setAttribute('hidden', '');
                        toc.classList.add('ca-toc--collapsed');
                    }
                }
            });
        });
    }

    /**
     * Initialize active section highlighting
     */
    function initActiveHighlight(toc) {
        var links = toc.querySelectorAll('.ca-toc__link');
        var headings = [];
        
        // Build array of heading elements
        links.forEach(function(link) {
            var href = link.getAttribute('href');
            if (href && href.charAt(0) === '#') {
                var heading = document.getElementById(href.slice(1));
                if (heading) {
                    headings.push({
                        element: heading,
                        link: link
                    });
                }
            }
        });
        
        if (headings.length === 0) return;
        
        // Use Intersection Observer for better performance
        if ('IntersectionObserver' in window) {
            var observer = new IntersectionObserver(function(entries) {
                entries.forEach(function(entry) {
                    var link = headings.find(function(h) {
                        return h.element === entry.target;
                    });
                    
                    if (link) {
                        if (entry.isIntersecting) {
                            setActiveLink(links, link.link);
                        }
                    }
                });
            }, {
                rootMargin: '-100px 0px -66% 0px',
                threshold: 0
            });
            
            headings.forEach(function(h) {
                observer.observe(h.element);
            });
        } else {
            // Fallback to scroll listener
            var scrollHandler = debounce(function() {
                var scrollPosition = window.scrollY + getHeaderOffset() + 20;
                var activeHeading = null;
                
                headings.forEach(function(h) {
                    if (h.element.offsetTop <= scrollPosition) {
                        activeHeading = h;
                    }
                });
                
                if (activeHeading) {
                    setActiveLink(links, activeHeading.link);
                }
            }, 50);
            
            window.addEventListener('scroll', scrollHandler);
            scrollHandler(); // Run once on init
        }
    }

    /**
     * Set active link and remove from others
     */
    function setActiveLink(allLinks, activeLink) {
        allLinks.forEach(function(link) {
            link.classList.remove('is-active');
        });
        activeLink.classList.add('is-active');
    }

    /**
     * Get header offset for scroll calculations
     */
    function getHeaderOffset() {
        var header = document.querySelector('.site-header, header, [role="banner"]');
        if (header) {
            var style = window.getComputedStyle(header);
            if (style.position === 'fixed' || style.position === 'sticky') {
                return header.offsetHeight + 20;
            }
        }
        return 100; // Default offset
    }

    /**
     * Debounce helper function
     */
    function debounce(func, wait) {
        var timeout;
        return function() {
            var context = this;
            var args = arguments;
            clearTimeout(timeout);
            timeout = setTimeout(function() {
                func.apply(context, args);
            }, wait);
        };
    }
})();

